import { Column } from "primereact/column";
import { SharedTableColumnConfig } from "../types/SharedTableColumnConfig";
import { columnWidth } from "./DefectsTableDataHelper";
import {
    columnTemplates,
    filterTemplates,
    validInspectionTypes
} from "../components/DefectsTable/Constants";
import {
    notesEditorTemplate,
    notesPlaceholderTemplate
} from "../components/DefectsTable/BodyTemplates";
import { DisplayModalContent } from "../components/Shared/DisplayModal";
import { DefectDataItem } from "../types/DefectsDataItem";
import { Inspection } from "../types/Inspection";
import { InspectionAndGradeDataResponse } from "../types/InspectionAndGradeDataResponse";

export const renderColumnComponents = (
    columnConfig: SharedTableColumnConfig[],
    handleColumnUpdate: (defectId: number, updatedColumn: string, value: any) => void,
    handleReadMoreClick: (content: DisplayModalContent) => void,
    selectVessel: (rowData: any) => void,
    filteredData: DefectDataItem[],
    onCellEditComplete: (e: any) => Promise<void>,
) => {
    const result = columnConfig.map((col) => {
        const columnStyle = {
            ...columnWidth(col.field),
            WhiteSpace: "normal"
        };
        return (
            <Column
                key={col.field}
                field={col.field}
                dataType={col.dataType}
                header={col.header}
                filterMatchMode={col.filterMatchMode}
                frozen={col.frozen}
                className={col.showFilterMenu ?? false ? "has-filter-menu" : ""}
                headerClassName={`${col.field
                    .split(".")
                    .slice(-1)
                    .pop()}-header`}
                sortable={col.sortable ?? false}
                showFilterMenu={col.showFilterMenu ?? false}
                showFilterMatchModes={col.showFilterMatchModes ?? false}
                filter={col.filter ?? false}
                filterField={col.filterField}
                filterFunction={col.filterFunction}
                sortFunction={col?.sortFunction}
                body={
                    columnTemplates(
                        handleColumnUpdate,
                        handleReadMoreClick,
                        selectVessel
                    ).find((e: any) => e.column === col.field)?.bodyTemplate ??
                    null
                }
                bodyStyle={{
                    textAlign: col.textAlign ?? "left",
                    justifyContent: col.justifyContent ?? "start"
                }}
                style={columnStyle}
                filterElement={
                    filterTemplates(filteredData).find(
                        (e) => e.column === col.field
                    )?.filterTemplate ?? null
                }
                filterPlaceholder={col.filterPlaceholder ?? ""}
                editor={
                    col.field === "notes"
                        ? (rowData) => notesEditorTemplate(rowData)
                        : ""
                }
                onBeforeCellEditShow={notesPlaceholderTemplate}
                onCellEditComplete={onCellEditComplete}
                exportable={col.exportable ?? false}
            />
        );
    });
    return result;
};

export const filterByInspectionType = (inspection: Inspection | undefined) => {
    if (inspection?.inspectionType) {
        const { shortName } = inspection.inspectionType as { shortName: string };

        if (shortName) {
            return validInspectionTypes.includes(shortName);
        }
    }

    if (inspection?.inspectionTypeShortName) {
        return validInspectionTypes.includes(inspection.inspectionTypeShortName);
    }

    return false;
};

export const getInspectionIdsForDefects = (
    inspectionsData: InspectionAndGradeDataResponse
) => {

    if (!inspectionsData?.fleetDataItems.length) {
        return [];
    }

    const inspections = new Set<string>();

    inspectionsData.fleetDataItems.forEach((item) => {
        if (item.inspection.guid) {
            if (filterByInspectionType(item.inspection)) {
                inspections.add(item.inspection.guid);
            }

            item.scoreHistory?.forEach((historicInspection) => {
                if (historicInspection.id) {
                    if (filterByInspectionType(historicInspection)) {
                        inspections.add(historicInspection.id);
                    }

                    // edge case where PSI inspection has a CONFIRMED IDCI scheduled 
                    // and the type is swapped in getByVessels in inspection-service
                    if (
                        historicInspection.id === item.inspection.guid &&
                        !filterByInspectionType(historicInspection)
                    ) {
                        inspections.delete(historicInspection.id);
                    }
                }
            });
        }
    });

    return Array.from(inspections);
};
